import { Button, Checkbox } from "@legal-500/legal500-design-system";
import { Fragment, useCallback, useState } from "react";
import * as api from "../../../api/client.js";
import { researchCms } from "../../../api/researchCms.service.js";
import { SearchableSelect } from "../../../components/Form/SearchableSelect/SearchableSelect.js";
import { useQuery } from "../../../hooks/useApi.js";
import styles from "./ResearchDataImport.module.css";

const DEFAULT_EDITION_NAME = "Legal 500 EMEA 2027";

type UnmatchedRow = { country: string; practiceArea: string; researcher: string; email: string };
type ConflictRow = {
  country: string;
  practiceArea: string;
  email: string;
  currentResearcherId: string;
};

export type ResearcherAllocationsResponse = {
  editionId: string;
  overwrite: boolean;
  rowsRead: number;
  assigned: number;
  overwritten: number;
  unchanged: number;
  conflicts: number;
  skippedUnmatched: number;
  skippedUnknownEmail: number;
  duplicateTargets: number;
  unmatchedRows: UnmatchedRow[];
  unknownEmails: string[];
  conflictRows: ConflictRow[];
  logs: string[];
  requestId: string;
};

const COUNT_FIELDS = [
  ["rowsRead", "Rows read"],
  ["assigned", "Assigned"],
  ["overwritten", "Overwritten"],
  ["unchanged", "Unchanged"],
  ["conflicts", "Left as is: already assigned to someone else"],
  ["skippedUnmatched", "Left unassigned: BPA not found in edition"],
  ["skippedUnknownEmail", "Left unassigned: no researcher account for email"],
  ["duplicateTargets", "Duplicate rows"],
] as const;

export function ResearcherAllocationsImport() {
  const { data: editionData } = useQuery(() => researchCms.getEditions(), []);
  const editionConfigs = editionData?.editionConfigs ?? [];
  const [selectedEditionId, setSelectedEditionId] = useState<string | null>(null);
  const defaultEditionId =
    editionConfigs.find(config => config.editionName === DEFAULT_EDITION_NAME)?.editionId ?? null;
  const editionId = selectedEditionId ?? defaultEditionId;

  const [overwrite, setOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ResearcherAllocationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImport = useCallback(async () => {
    if (!editionId) return;
    setImporting(true);
    setResult(null);
    setError(null);
    try {
      const res = await api.post<ResearcherAllocationsResponse>(
        "/v1/research/seed/researcher-allocations",
        { editionId, overwrite },
      );
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }, [editionId, overwrite]);

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>Researcher Allocations</h2>
      <p className={styles.sectionDescription}>
        Assign researchers to the selected edition&apos;s BPAs from the checked-in EMEA 2027
        allocations CSV. Rows are matched by country and practice area name; emails are matched
        against users with the researcher role. Unmatched rows are listed, never guessed.
      </p>

      <div className={styles.formRow}>
        <div className={styles.formField}>
          <SearchableSelect
            name="researcher-allocations-edition"
            label="Edition"
            value={editionId}
            options={editionConfigs.map(config => ({
              value: config.editionId,
              label: `${config.editionName ?? config.editionId} — ${config.status}`,
            }))}
            onChange={value => setSelectedEditionId(value as string | null)}
            placeholder="Select edition..."
            preserveOptionOrder
          />
        </div>
        <div className={styles.checkboxGroup}>
          <Checkbox
            label="Overwrite existing assignments"
            aria-label="Overwrite existing assignments"
            checked={overwrite}
            onChange={e => setOverwrite(e.target.checked)}
          />
        </div>
        <div className={styles.formAction}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={importing || !editionId}
          >
            {importing ? "Importing..." : "Import Allocations"}
          </Button>
        </div>
      </div>

      {error && <div className={`${styles.notification} ${styles.error}`}>{error}</div>}

      {result && (
        <div className={styles.resultCard}>
          <h3>Import Complete</h3>
          <dl className={styles.resultGrid}>
            {COUNT_FIELDS.map(([key, label]) => (
              <Fragment key={key}>
                <dt>{label}</dt>
                <dd>{result[key]}</dd>
              </Fragment>
            ))}
          </dl>

          {result.unmatchedRows.length > 0 && (
            <details className={styles.logsDetails} open>
              <summary>
                Left unassigned: BPA not found in this edition ({result.unmatchedRows.length})
              </summary>
              <div className={styles.tableContainer}>
                <table>
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Practice area</th>
                      <th>Researcher</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.unmatchedRows.map(row => (
                      <tr key={`${row.country}|${row.practiceArea}|${row.email}`}>
                        <td>{row.country}</td>
                        <td>{row.practiceArea}</td>
                        <td>{row.researcher}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {result.unknownEmails.length > 0 && (
            <details className={styles.logsDetails} open>
              <summary>
                Left unassigned: no researcher account for these emails (
                {result.unknownEmails.length})
              </summary>
              <pre className={styles.logContent}>{result.unknownEmails.join("\n")}</pre>
            </details>
          )}

          {result.conflictRows.length > 0 && (
            <details className={styles.logsDetails} open>
              <summary>
                Left as is: already assigned to someone else ({result.conflictRows.length})
              </summary>
              <div className={styles.tableContainer}>
                <table>
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Practice area</th>
                      <th>CSV email</th>
                      <th>Current researcher id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.conflictRows.map(row => (
                      <tr key={`${row.country}|${row.practiceArea}|${row.email}`}>
                        <td>{row.country}</td>
                        <td>{row.practiceArea}</td>
                        <td>{row.email}</td>
                        <td>{row.currentResearcherId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {result.logs.length > 0 && (
            <details className={styles.logsDetails}>
              <summary>Logs ({result.logs.length} entries)</summary>
              <pre className={styles.logContent}>{result.logs.join("\n")}</pre>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
